"use client";

type ErrorAlertProps = {
  /** Mensagem de erro ou null */
  error: string | null;
  /** Callback opcional de retry — se passado, exibe botão "Tentar novamente" */
  onRetry?: () => void;
  /** Label alternativo para o botão de retry */
  retryLabel?: string;
  /** Se está em estado de retry (mostra spinner) */
  retrying?: boolean;
  className?: string;
};

/**
 * Exibe mensagem de erro com suporte a botão de retry.
 * Projetado para ser usado em formulários e telas com chamadas assíncronas.
 *
 * Uso simples:
 *   <ErrorAlert error={error} />
 *
 * Com retry:
 *   <ErrorAlert error={error} onRetry={handleRetry} retrying={loading} />
 */
export function ErrorAlert({
  error,
  onRetry,
  retryLabel = "Tentar novamente",
  retrying = false,
  className = "",
}: ErrorAlertProps) {
  if (!error) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive ${className}`}
    >
      <p>{error}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-4 hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {retrying ? (
            <>
              <svg
                className="h-3.5 w-3.5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Tentando…
            </>
          ) : (
            <>↻ {retryLabel}</>
          )}
        </button>
      )}
    </div>
  );
}
